function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import MapprComponent from '../components/Mappr';
import { useDispatch, useSelector } from 'react-redux';
import { Provider } from 'react-redux';
import store from '../store';
import { appInit, appSetLanguage } from '../store/actions/appSlice';
import { login, loginDone, logout } from '../store/actions/authSlice';
import ApolloClient from '../apollo';
import useAnalytics from '../hooks/useAnalytics';

/**
 * This is the main component needed to initiate a Mappr application.
 * All components from the library shouold be nested under a single instance of this component.
 *  */
function MapprWrapper(props) {
  return /*#__PURE__*/React.createElement(Provider, {
    store: store
  }, /*#__PURE__*/React.createElement(MapprContainer, props));
}
function MapprContainer(props) {
  var dispatch = useDispatch();
  var appState = useSelector(function (state) {
    return state.app;
  });
  var authState = useSelector(function (state) {
    return state.auth;
  });
  var theUser = authState.theUser;
  var project = appState.project,
    basemaps = appState.basemaps,
    secondaryLayers = appState.secondaryLayers,
    appLoading = appState.loading;
  useEffect(function () {
    ApolloClient.init({
      url: props.mprGqlUrl,
      domain: props.name
    });
    dispatch(appInit({
      domain: props.name,
      language: props.language,
      mprApiUrl: props.mprApiUrl,
      mprTileUrl: props.mprTileUrl
    }));
    return function () {
      var _ApolloClient$client;
      (_ApolloClient$client = ApolloClient.client) === null || _ApolloClient$client === void 0 ? void 0 : _ApolloClient$client.stop();
    };
  }, []);
  useEffect(function () {
    if (!project || appLoading) {
      return;
    }
    var authSession = localStorage.getItem("mpr." + project.id + ".auth");
    if (authSession) {
      authSession = JSON.parse(authSession);

      // const expirationDate = authSession.expiresAt;
      // const currentDate = new Date();
      // if (Date.parse(currentDate) < Date.parse(expirationDate)) {
      dispatch(loginDone(_extends({}, authSession, {
        projectId: project.id
      })));
      // } else {
      //   this.props.onLogout({ userId: authSession.user.id, projectId });
      // }
    }
  }, [project, appLoading]);
  useEffect(function () {
    dispatch(appSetLanguage({
      language: props.language
    }));
  }, [props.language]);
  useAnalytics();
  return /*#__PURE__*/React.createElement(MapprComponent, _extends({}, props, {
    language: appState.language,
    error: authState.error || appState.error,
    project: project,
    basemaps: basemaps,
    secondaryLayers: secondaryLayers,
    loading: appLoading,
    theUser: theUser,
    onLogin: function onLogin(_ref) {
      var username = _ref.username,
        password = _ref.password;
      return dispatch(login({
        username: username,
        password: password
      }));
    },
    onLogout: function onLogout() {
      return dispatch(logout({
        userId: theUser && theUser.id
      }));
    },
    onSetLanguage: function onSetLanguage(language) {
      return dispatch(appSetLanguage({
        language: language
      }));
    }
  }));
}
MapprWrapper.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The project unique domain name */
  name: PropTypes.string.isRequired,
  /** The main url of Mappr GraphQL API */
  mprGqlUrl: PropTypes.string.isRequired,
  /** The main url of Mappr Rest API */
  mprApiUrl: PropTypes.string.isRequired,
  /** The main url of Mappr Rest API */
  mprTileUrl: PropTypes.string.isRequired,
  /** The current application language to be applied on data fetching */
  language: PropTypes.string.isRequired,
  /**
   * A render function that is used to display the a UI based on the provided data and methods.  Either the single function as a child this prop should be provided
   * @function
   * @param {Object} props
   * @param {boolean} props.loading  A loading state identifier
   * @param {Object} props.error An error object if any
   * @param {boolean} props.basemaps The list of available basemaps
   * @param {Array} props.secondaryLayers The list of available secondary layers
   * @param {Object} props.project The project configuration object
   * @param {boolean} props.authenticated The auth state for the current end user
   * @param {string} props.language The currently set language
   * @param {Object} methods
   * @param {function} methods.login A method to call when logging in
   * @param {function} methods.logout A method to call when logging out
   * @param {function} methods.setLanguage A method to call when changing the app language
   *  */
  render: PropTypes.func
} : {};
MapprWrapper.defaultProps = {
  mprGqlUrl: 'https://webapi.mappr.xyz/graphql',
  mprApiUrl: 'https://webapi.mappr.xyz/graphql',
  mprTileUrl: 'https://api.mappr.xyz'
};
MapprContainer.propTypes = process.env.NODE_ENV !== "production" ? _extends({}, MapprWrapper.propTypes) : {};
MapprContainer.defaultProps = _extends({}, MapprWrapper.defaultProps);
export { MapprWrapper as default };
export var Mappr = MapprWrapper;